/**
 * In-app notifications.
 *
 * The legacy app had three parallel notification systems — a global one in
 * app.js, a per-patient one in patient_dashboard.js and a per-doctor one in
 * doctor_dashboard.js — each with its own storage key, its own shape and its own
 * badge logic. They are unified here into one model with a scope, which is the
 * only thing that genuinely differed between them.
 */

export const NOTIFICATION_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger',
};

/** Lucide icon per type — the legacy `getNotificationIcon`. */
export const NOTIFICATION_ICONS = {
  [NOTIFICATION_TYPES.INFO]: 'info',
  [NOTIFICATION_TYPES.SUCCESS]: 'check-circle-2',
  [NOTIFICATION_TYPES.WARNING]: 'alert-triangle',
  [NOTIFICATION_TYPES.DANGER]: 'alert-octagon',
};

export const NOTIFICATION_STYLES = {
  [NOTIFICATION_TYPES.INFO]: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  [NOTIFICATION_TYPES.SUCCESS]: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  [NOTIFICATION_TYPES.WARNING]: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  [NOTIFICATION_TYPES.DANGER]: 'text-red-400 bg-red-500/10 border-red-500/30',
};

export const notificationIcon = (type) =>
  NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS[NOTIFICATION_TYPES.INFO];

export const notificationStyle = (type) =>
  NOTIFICATION_STYLES[type] || NOTIFICATION_STYLES[NOTIFICATION_TYPES.INFO];

/**
 * Storage key for a notification scope.
 * `global` keeps the legacy key so existing entries survive the upgrade.
 */
export function notificationStorageKey(scope, ownerId) {
  if (scope === 'global' || !ownerId) return 'capsule_notifications';
  return `capsule_${scope}_notifications_${ownerId}`;
}

let sequence = 0;

/** Build a notification. Ids combine the clock with a counter so a burst stays unique. */
export function createNotification({
  type = NOTIFICATION_TYPES.INFO,
  title,
  message,
  action = null,
  icon = null,
}) {
  sequence += 1;
  return {
    id: `${Date.now()}-${sequence}`,
    type,
    title,
    message,
    action,
    icon: icon || notificationIcon(type),
    read: false,
    createdAt: new Date().toISOString(),
  };
}

/** Newest first, capped — the legacy list grew without limit and got slow. */
export const MAX_NOTIFICATIONS = 100;

export function addNotification(list = [], notification) {
  return [notification, ...list].slice(0, MAX_NOTIFICATIONS);
}

export const markRead = (list = [], id) =>
  list.map((n) => (n.id === id ? { ...n, read: true } : n));

export const markAllRead = (list = []) => list.map((n) => ({ ...n, read: true }));

export const removeNotification = (list = [], id) => list.filter((n) => n.id !== id);

export const unreadCount = (list = []) => list.filter((n) => !n.read).length;

/** "9+" once the count passes nine, matching the legacy badge. */
export const formatBadge = (count) => (count > 9 ? '9+' : String(count));

/**
 * Resolve a notification's `action` string to a route.
 *
 * The legacy format is "<role>:<section>", e.g. "doctor:schedule". Anything that
 * already looks like a path is passed through untouched.
 */
export function actionToRoute(action) {
  if (!action) return null;
  if (action.startsWith('/')) return action;

  const [role, section] = action.split(':');
  if (!section) return `/${role}`;

  const routes = {
    doctor: `/doctor/${section}`,
    patient: `/patient/${section}`,
    admin: `/admin/${section}`,
    team: `/team/${section}`,
  };
  return routes[role] || null;
}

/**
 * Collapse a burst of same-kind notifications into one summary line.
 *
 * The legacy `queueBatchNotification` / `processBatchNotifications` pair existed
 * because a sync tick could otherwise fire a dozen "new booking" toasts at once.
 */
export function batchNotifications(items = [], { type, title, singular, plural }) {
  if (items.length === 0) return null;
  if (items.length === 1) {
    return createNotification({ type, title, message: singular(items[0]) });
  }
  return createNotification({ type, title, message: plural(items.length, items) });
}
